import React, { useState, useEffect } from 'react';  
import { useNavigate } from 'react-router-dom';  
import { ROUTES } from '#root/routes/constants';  
import './styles.scss';  
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { Button, Dropdown, Field, Option } from '@fluentui/react-components';
import ButtonGroup from '#root/components/ButtonGroup';
import { LANGUAGE_OPTIONS_LIST, UNIT_OPTIONS, LANGUAGE_OPTIONS } from '#root/utils/settings';
import { useTenantById, useUpdateTenantDefaultUnits } from '#root/queries/tenants';
import { showToast } from '#root/components/GlobalToaster';
import { useCurrentTenant } from '#root/queries/local';
import LoadingWrapper from '#root/components/LoadingWrapper';
import { useDispatch } from 'react-redux';
import { loadCurrentUser } from '#root/redux/auth/actions';

const DefaultUnitsSettings: React.FC = () => {  
    const navigate = useNavigate();  
    const dispatch = useDispatch();
    const { data: tenantId } = useCurrentTenant();
    const { data: tenantResponse, isLoading: tenantLoading } = useTenantById({ 
        id: tenantId || '' 
    });
    const updateTenantDefaultUnits = useUpdateTenantDefaultUnits();

    const tenant = tenantResponse?.item;

    // Initialize state with tenant data or defaults
    const [unit, setUnit] = useState(tenant?.unit || UNIT_OPTIONS.METRIC);
    const [language, setLanguage] = useState(tenant?.language || LANGUAGE_OPTIONS.ENGLISH);

    // Update state when tenant data loads
    useEffect(() => {
        if (tenant) {
            setUnit(tenant.unit!);
            setLanguage(tenant.language!);
        }
    }, [tenant]);

    useEffect(() => {  
        if (!tenantId) {  
            navigate(ROUTES.ERROR_401);
        }  
    }, [tenantId, navigate]);  

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tenant) return;

        try {
            await updateTenantDefaultUnits.mutateAsync({
                unit,
                language
            });

            showToast('Default units updated successfully', 'success');
            dispatch(loadCurrentUser());
        } catch (error) {
            showToast('Failed to update default units', 'error');
        }
    };
  
    return (  
        <div className="default-units-settings">  
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs/>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Default Units <div className="divider"></div></h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={tenantLoading}
                isEmpty={false}
                emptyMessage=""
            >
                <form className="default-units-form" onSubmit={handleSubmit}>
                    <Field label="Units">
                        <ButtonGroup
                            name="units"
                            options={Object.entries(UNIT_OPTIONS).map(([_, value]) => ({
                                label: value,
                                value: value
                            }))}
                            onChange={(value) => setUnit(value)}
                            value={unit}
                            required
                        />
                    </Field>
                    <Field label="Language">
                        <Dropdown
                            value={language}
                            selectedOptions={[language]}
                            onOptionSelect={(_,data) => {
                                const value = data.selectedOptions[0];
                                if (value) {
                                    setLanguage(value);
                                }
                            }}
                        >
                            {LANGUAGE_OPTIONS_LIST.map(lang => (
                                <Option key={lang} value={lang} text={lang}>
                                    {lang}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>

                    <div className="form-actions">
                        <Button
                            type="submit"
                            appearance="primary"
                            size="large"
                            disabled={updateTenantDefaultUnits.isPending}
                        >
                            {updateTenantDefaultUnits.isPending ? 'Saving...' : 'Save'}
                        </Button>
                        <Button 
                            appearance="secondary" 
                            size="large"
                            onClick={() => navigate(ROUTES.ORGANIZATION_SETTINGS.ROOT)}
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </LoadingWrapper>
        </div>  
    );  
};  
  
export default DefaultUnitsSettings;